import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Immutable from 'immutable';
import { Tabs, TabContent } from '@bibliocommons/deprecated-base-tabs';
import { bindAll } from '@bibliocommons/utils-react';
import BibBrief from 'app/components/shared/BibBrief';
import FullScreenOverlay from '@bibliocommons/deprecated-base-full-screen-overlay';
import JacketCover from '@bibliocommons/shared-jacket-cover';
import { Placeholder } from '@bibliocommons/deprecated-base-table';
import FormatIndicator from '@bibliocommons/format-indicator';
import { AlertFailure } from '@bibliocommons/deprecated-base-alerts';
import { bibJacketCoverAltText } from '@bibliocommons/utils-item';

import Context from '@bibliocommons/context';
import AvailabilityTable from '../AvailabilityTable';
import HoldingsProbeContainer from '../HoldingsProbe/HoldingsProbeContainer';
import StackMapContainer from '../StackMap/StackMapContainer';
import MarcHoldingsTable from '../MarcHoldingsTable';
import SubscriptionTable from '../SubscriptionTable';
import EResourceBlock from '../EResourceBlock';
import './AvailabilityDetailOverlay.scss';

const propTypes = {
  bib: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  disableClose: PropTypes.bool,
  open: PropTypes.bool,

  availabilityDetail: PropTypes.shape({
    marcHoldings: PropTypes.object,
    subscriptions: PropTypes.object,
    items: PropTypes.object,
    format: PropTypes.string,
    jacket: PropTypes.object,
    title: PropTypes.string,
    subtitle: PropTypes.string
  }),
  stackMap: ImmutablePropTypes.mapContains({
    enabled: PropTypes.bool,
    branches: ImmutablePropTypes.listOf(PropTypes.string),
    domain: PropTypes.string
  }),
  stackMapOpen: PropTypes.bool.isRequired,
  openStackMap: PropTypes.func.isRequired,
  showDueDateInCirc: PropTypes.bool,
  showCollectionInCirc: PropTypes.bool,
  isILSDown: PropTypes.bool,
  isFetching: PropTypes.bool
};

export class AvailabilityDetailOverlay extends React.PureComponent {
  constructor(props) {
    super(props);

    bindAll(this);
  }

  renderSubscriptionTables() {
    const { intl } = this.props;
    const { subscriptions } = this.props.availabilityDetail;
    if (subscriptions && subscriptions.size < 1) {
      return null;
    }

    return (
      <TabContent
        eventKey={1}
        label={intl.formatMessage({
          id: 'availability_modal_tab_subscription'
        })}
      >
        <SubscriptionTable subscriptions={subscriptions} />
      </TabContent>
    );
  }

  renderMarcHoldingsTables() {
    const { intl } = this.props;
    const { marcHoldings } = this.props.availabilityDetail;
    if (marcHoldings && marcHoldings.size < 1) {
      return null;
    }
    return (
      <TabContent
        eventKey={2}
        label={intl.formatMessage({
          id: 'availability_modal_tab_marc_holdings'
        })}
      >
        <h3>Marc Holdings</h3>
        <MarcHoldingsTable rows={marcHoldings} />
      </TabContent>
    );
  }

  renderAvailabilityTables() {
    const { intl, stackMap, openStackMap, showDueDateInCirc, showCollectionInCirc } = this.props;
    const itemGroup = this.props.availabilityDetail.items;
    if (!itemGroup || itemGroup.size < 1) {
      return null;
    }

    const tables = itemGroup.map(availability => (
      <AvailabilityTable
        stackMap={stackMap}
        openStackMap={openStackMap}
        key={availability.get('key')}
        status={availability.get('status')}
        rows={availability.get('items')}
        showDueDateInCirc={showDueDateInCirc}
        showCollectionInCirc={showCollectionInCirc}
      />
    ));

    return (
      <TabContent eventKey={0} label={intl.formatMessage({ id: 'availability_modal_tab_circulation' })}>
        {tables}
      </TabContent>
    );
  }

  renderEResources() {
    const url = this.props.bib.getIn(['availability', 'eresourceUrl']);
    const description = this.props.bib.getIn(['availability', 'eresourceDescription']);
    if (url) {
      // TODO: This should be updated to use pass in all eResources instead
      const eResources = Immutable.List.of(Immutable.Map({ description, url }));
      return <EResourceBlock eResources={eResources} format={this.props.bib.getIn(['briefInfo', 'format'])} />;
    }

    return null;
  }

  renderOverlayContent() {
    if (this.props.open) {
      const { bib, isILSDown, isFetching } = this.props;
      const bibCoverAltText = bibJacketCoverAltText(
        bib.getIn(['briefInfo', 'title']),
        bib.getIn(['briefInfo', 'authors'])
      );

      return (
        <div>
          <FormattedMessage tagName="h2" id="availabilityFor" />
          <div className="availability-title-block">
            <div className="availability-title-block-jacket">
              <JacketCover
                src={bib.getIn(['briefInfo', 'jacket'])}
                format={bib.getIn(['briefInfo', 'format'])}
                alt={bibCoverAltText}
              />
            </div>
            <div className="availability-title-block-brief">
              <BibBrief bib={bib.get('briefInfo')} titleTag="h1" renderAuthorsAsLink={!this.context.mobileApp} />
              <FormatIndicator format={bib.getIn(['briefInfo', 'format'])} />
              <span> &ndash; {bib.getIn(['briefInfo', 'publicationDate'])}</span>
              <br />
              <span>
                {this.props.intl.formatMessage(
                  { id: 'totalCopies' },
                  { totalCopies: bib.getIn(['availability', 'totalCopies']) }
                )}
              </span>
            </div>
            <hr className="first" />
            <HoldingsProbeContainer id={this.props.bib.get('id')} />
          </div>
          <hr className="second" />
          {isILSDown && !isFetching && (
            <AlertFailure>
              <FormattedMessage id="errors_ils_item_availability_error" />
            </AlertFailure>
          )}
          {this.renderEResources()}
          {this.renderTabs()}
        </div>
      );
    }

    return null;
  }

  renderTabs() {
    if (this.props.isFetching) {
      return <Placeholder />;
    }

    return (
      <Tabs defaultActiveKey={0}>
        {this.renderAvailabilityTables()}
        {this.renderSubscriptionTables()}
      </Tabs>
    );
  }

  render() {
    const { disableClose, open, stackMapOpen, handleClose } = this.props;

    return (
      <FullScreenOverlay
        open={open}
        disableClose={disableClose || stackMapOpen}
        handleClose={handleClose}
        className="cp-availability-details-overlay"
      >
        {this.renderOverlayContent()}
        <StackMapContainer />
      </FullScreenOverlay>
    );
  }
}

AvailabilityDetailOverlay.propTypes = propTypes;

AvailabilityDetailOverlay.contextType = Context;

export default injectIntl(AvailabilityDetailOverlay);
